import { STORAGE } from '../constants'

export interface FormatOutcome {
  successes: number
  failures: number
  lastSuccess?: number
  lastFailure?: number
  lastError?: string
}

export type CompatibilityEvidence = Record<string, FormatOutcome>

function record(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function cleanCount(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.max(0, Math.min(20, Math.floor(value))) : 0
}

function cleanTimestamp(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.min(value, 9_999_999_999_999) : undefined
}

function cleanOutcome(value: unknown): FormatOutcome | null {
  if (!record(value)) return null
  const successes = cleanCount(value.successes)
  const failures = cleanCount(value.failures)
  const lastSuccess = cleanTimestamp(value.lastSuccess)
  const lastFailure = cleanTimestamp(value.lastFailure)
  const lastError = typeof value.lastError === 'string'
    ? value.lastError.replace(/https?:\/\/\S+/gi, '[media URL]').slice(0, 160) : undefined
  if (!successes && !failures && !lastSuccess && !lastFailure) return null
  return { successes, failures, lastSuccess, lastFailure, lastError }
}

function runtimeKey() {
  if (typeof navigator === 'undefined') return 'test-runtime'
  const model = (globalThis as typeof globalThis & { PalmSystem?: { deviceInfo?: string } }).PalmSystem?.deviceInfo || ''
  return `${navigator.userAgent}|${model}`.slice(0, 500)
}

function storageKey() {
  let hash = 0x811c9dc5
  const value = runtimeKey()
  for (let index = 0; index < value.length; index += 1) hash = Math.imul(hash ^ value.charCodeAt(index), 0x01000193)
  return `${STORAGE.compatibility}.${(hash >>> 0).toString(36)}`
}

export function getCompatibilityEvidence(): CompatibilityEvidence {
  if (typeof localStorage === 'undefined') return {}
  try {
    const value = JSON.parse(localStorage.getItem(storageKey()) || '{}') as unknown
    if (!record(value)) return {}
    return Object.fromEntries(Object.entries(value).flatMap(([key, outcome]) => {
      const clean = key.length > 0 && key.length <= 120 ? cleanOutcome(outcome) : null
      return clean ? [[key, clean] as const] : []
    }).sort((a, b) => Math.max(b[1].lastSuccess || 0, b[1].lastFailure || 0) - Math.max(a[1].lastSuccess || 0, a[1].lastFailure || 0)).slice(0, 80))
  } catch { return {} }
}

export function compatibilityScore(key: string, evidence: CompatibilityEvidence = {}) {
  const outcome = cleanOutcome(evidence[key])
  if (!outcome) return { score: 0, risk: '', proof: '' }
  // A repeated failure on this exact webOS runtime must outweigh a generic
  // canPlayType "probably" answer. A later successful fallback can recover.
  const score = Math.min(24, outcome.successes * 8) - Math.min(90, outcome.failures * 32)
  const risk = outcome.failures > outcome.successes
    ? `Failed ${outcome.failures} time${outcome.failures === 1 ? '' : 's'} on this TV`
    : ''
  const proof = outcome.successes && outcome.successes >= outcome.failures
    ? 'Played on this TV'
    : ''
  return { score, risk, proof }
}

export function recordCompatibilityOutcome(key: string, result: 'success' | 'failure', error = '') {
  if (typeof localStorage === 'undefined' || !key) return
  const safeKey = key.slice(0, 120)
  const evidence = getCompatibilityEvidence()
  const previous = evidence[safeKey] || { successes: 0, failures: 0 }
  const next: FormatOutcome = result === 'success'
    ? { ...previous, successes: Math.min(20, previous.successes + 1), lastSuccess: Date.now() }
    : {
      ...previous,
      failures: Math.min(20, previous.failures + 1),
      lastFailure: Date.now(),
      lastError: error.replace(/https?:\/\/\S+/gi, '[media URL]').slice(0, 160),
    }
  const entries = Object.entries({ ...evidence, [safeKey]: next })
    .sort((a, b) => Math.max(b[1].lastSuccess || 0, b[1].lastFailure || 0) - Math.max(a[1].lastSuccess || 0, a[1].lastFailure || 0))
    .slice(0, 80)
  try { localStorage.setItem(storageKey(), JSON.stringify(Object.fromEntries(entries))) } catch { /* storage full */ }
}

export function clearCompatibilityEvidence() {
  if (typeof localStorage !== 'undefined') localStorage.removeItem(storageKey())
}
